import { useTranslation } from 'react-i18next';
import { ArrowLeft } from 'lucide-react';
import { changelogForLocale } from '@evi/content';
import { HelpTabs } from './HelpTabs';
import { markEvie, formatDate } from './helpFormat';
import styles from './HelpPage.module.css';

interface Props {
  onNavigate: (path: string) => void;
}

/**
 * What's New — the changelog, reached at `/help/whats-new` from the Help
 * table of contents. Renders CHANGELOG_ENTRIES newest first, the same source
 * EVII reads for "what is new" questions.
 */
export function HelpChangelogPage({ onNavigate }: Props) {
  const { t, i18n } = useTranslation('help');
  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <button type="button" className={styles.backFab} onClick={() => onNavigate('/help')} aria-label={t('backToHelp')}>
          <ArrowLeft size={18} />
        </button>
        <div className={styles.titleBlock}>
          <h1 className={styles.title}>{t('changelog.title')}</h1>
          <p className={styles.subtitle}>{t('changelog.subtitle')}</p>
        </div>
      </header>

      <HelpTabs active="whats-new" onNavigate={onNavigate} />

      <div className={styles.list}>
        {changelogForLocale(i18n.language).map((entry) => {
          // Every update renders as a bullet list, one item per idea, so the
          // whole changelog reads cleanly instead of as prose blocks. This is
          // the default What's New style. Entries carry one idea per line
          // (\n-joined in @evi/content); a single-idea entry is one bullet.
          const features = entry.body.split('\n').map((l) => l.trim()).filter(Boolean);
          return (
            <article key={`${entry.date}-${entry.title}`} className={styles.card}>
              <time className={styles.entryDate} dateTime={entry.date}>
                {formatDate(entry.date)}
              </time>
              <h2 className={styles.cardTitle}>{markEvie(entry.title)}</h2>
              <ul className={styles.cardBullets}>
                {features.map((feature, i) => (
                  <li key={i} className={styles.bullet}>{markEvie(feature)}</li>
                ))}
              </ul>
            </article>
          );
        })}
      </div>
    </div>
  );
}
